/**
 * Admin › Integrations › Recarga da carteira (W8-DRIVER-PREPAID-WALLET, D459, ADR-0074): each country's top-up PSP slot of
 * the credential vault — the environment, whether it is on, and the WRITE-ONLY API key and webhook token (a masked hint +
 * a configured badge, never the secret). The slots come from the server (one per country pack with a top-up adapter), so
 * the Admin names no provider. The webhook path is shown for the owner to register at the PSP. Top-ups stay unavailable
 * until BOTH secrets are sealed (the server fails closed).
 */
import { type ReactElement, useCallback, useEffect, useState } from 'react';
import type { WalletTopUpSlotView } from '@vello/api-client';
import { Badge, Button, Field, Notice, Panel, Select, TextInput, FormRow, Stack, Row, Text, Ink } from '@vello/web-theme';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { useCountryName } from '../i18n/country-name';

function SlotForm({ slot, onSaved }: { slot: WalletTopUpSlotView; onSaved: (v: WalletTopUpSlotView) => void }): ReactElement {
  const { runtime } = useAdmin();
  const { t } = useT();
  const { fail, refusal: refusalOf } = useVocab();
  const countryName = useCountryName();
  const id = `wallet-slot-${slot.providerId}`;
  const [environment, setEnvironment] = useState<'sandbox' | 'production'>(slot.environment ?? 'sandbox');
  const [enabled, setEnabled] = useState(slot.enabled);
  const [apiKey, setApiKey] = useState('');
  const [token, setToken] = useState('');
  const [msg, setMsg] = useState<{ tone: 'success' | 'danger'; text: string } | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const tokenShort = token.trim() !== '' && token.trim().length < 16;

  const save = async (): Promise<void> => {
    setBusy(true);
    const res = await runtime.wallet.saveTopUpSlot(slot.providerId, { environment, enabled, ...(apiKey.trim() !== '' ? { apiKey: apiKey.trim() } : {}), ...(token.trim() !== '' ? { webhookToken: token.trim() } : {}) });
    setBusy(false);
    if (!res.ok) {
      setMsg({ tone: 'danger', text: refusalOf('refusal.admin.driver_wallet', res.error) ?? t('walletslot.error', { code: fail(res.error) }) });
      return;
    }
    setApiKey('');
    setToken('');
    setMsg({ tone: 'success', text: t('walletslot.saved') });
    onSaved(res.value);
  };

  const ready = slot.apiKey.configured && slot.webhookToken.configured && slot.enabled;
  return (
    <Stack gap={3}>
      <Row gap={2} align="center" wrap>
        <strong>{slot.providerId}</strong>
        <span>· {countryName(slot.country)}</span>
        <Badge tone={ready ? 'success' : 'warning'}>{t(ready ? 'walletslot.live' : 'walletslot.notLive')}</Badge>
      </Row>
      <Text as="p" role="caption" tone="tertiary">
        {t('walletslot.webhook', { path: slot.webhookPath })}
      </Text>
      <FormRow
        action={
          <Button type="button" disabled={busy || tokenShort} onClick={() => void save()}>{t('walletslot.save')}</Button>
        }
      >
        <Field label={t('walletslot.environment')} htmlFor={`${id}-env`}>
          <Select id={`${id}-env`} value={environment} onChange={(e) => setEnvironment(e.target.value === 'production' ? 'production' : 'sandbox')}>
            <option value="sandbox">{t('walletslot.env.sandbox')}</option>
            <option value="production">{t('walletslot.env.production')}</option>
          </Select>
        </Field>
        <Field label={t('walletslot.state')} htmlFor={`${id}-on`}>
          <Select id={`${id}-on`} value={enabled ? 'on' : 'off'} onChange={(e) => setEnabled(e.target.value === 'on')}>
            <option value="on">{t('walletslot.on')}</option>
            <option value="off">{t('walletslot.off')}</option>
          </Select>
        </Field>
        <Field label={t('walletslot.apiKey')} htmlFor={`${id}-key`} hint={slot.apiKey.configured ? t('walletslot.configured', { hint: slot.apiKey.maskedHint ?? '' }) : t('walletslot.missing')}>
          <TextInput id={`${id}-key`} type="password" autoComplete="off" value={apiKey} onChange={(e) => setApiKey(e.target.value)} />
        </Field>
        <Field label={t('walletslot.token')} htmlFor={`${id}-token`} hint={tokenShort ? t('walletslot.tokenShort') : slot.webhookToken.configured ? t('walletslot.configured', { hint: slot.webhookToken.maskedHint ?? '' }) : t('walletslot.missing')}>
          <TextInput id={`${id}-token`} type="password" autoComplete="off" value={token} onChange={(e) => setToken(e.target.value)} aria-invalid={tokenShort} />
        </Field>
      </FormRow>
      {msg !== undefined && <Notice tone={msg.tone}>{msg.text}</Notice>}
    </Stack>
  );
}

export function WalletTopUpPanel(): ReactElement | null {
  const { runtime } = useAdmin();
  const { t } = useT();
  const { fail } = useVocab();
  const [slots, setSlots] = useState<readonly WalletTopUpSlotView[] | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);

  const load = useCallback(async (): Promise<void> => {
    const res = await runtime.wallet.topUpSlots();
    if (res.ok) setSlots(res.value.slots);
    // No `integration:view`, or a server without the wallet (404): the panel stays hidden rather than alarming.
    else setError(res.error.code === 'FORBIDDEN' || res.error.code === 'NOT_FOUND' ? undefined : t('walletslot.readError', { code: fail(res.error) }));
  }, [runtime, t]);

  useEffect(() => {
    void load();
  }, [load]);

  if (slots === undefined && error === undefined) return null;
  return (
    <Panel>
      <Text as="h3" role="title">{t('walletslot.title')}</Text>
      <Ink as="p" tone="secondary">{t('walletslot.subtitle')}</Ink>
      {error !== undefined && <Notice tone="danger">{error}</Notice>}
      {slots?.map((s) => (
        <SlotForm key={s.providerId} slot={s} onSaved={(v) => setSlots((all) => (all ?? []).map((x) => (x.providerId === v.providerId ? v : x)))} />
      ))}
    </Panel>
  );
}
